import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { DatetimeChangeEventDetail, IonDatetime, IonPopover } from "@ionic/react";
import { MouseEvent, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import CircleButton from "@components/buttons/CircleButton";
import BaseErrors from "@components/fields/BaseErrors";
import styles from "@components/forms/NextConsultForm.module.css";
import useOpener from "@hooks/useOpener";
import {
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { dateToMDYString } from "@utils/timeUtils";

type FormData = {
  nextConsult: string | null;
};

type Props = {
  careProfileId: string;
  nextConsult: string | null;
  nextConsultDraft: string | null;
};

const NextConsultForm = ({ careProfileId, nextConsult }: Props) => {
  let defaultNextConsultDate = null;
  const [popoverStateEvent, setPopoverStateEvent] = useState<MouseEvent<HTMLIonRouterLinkElement> | null>(null);
  const dateOpener = useOpener();
  if (nextConsult) {
    defaultNextConsultDate = dateToMDYString(nextConsult);
  }

  const defaultValues: FormData = {
    nextConsult: defaultNextConsultDate
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      nextConsult: yup.string().required(intl.formatMessage({ id: "errors.careProfile.nextConsult.blank" }))
    })
    .required();

  const { control, formState, handleSubmit, setError, watch } = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const nextConsultValue = watch("nextConsult");

  const onSubmit = useCallback(
    async (formData: FormData) => {
      setBaseErrors([]);
      return updateCareProfile({
        variables: {
          id: careProfileId,
          nextConsult: formData.nextConsult
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.careProfileUpdate,
          intl,
          onSuccess: () => {},
          setBaseErrors,
          setError,
          txBase: "errors.careProfile"
        });
      });
    },
    [updateCareProfile, careProfileId, intl, setError]
  );

  useEffect(() => {
    const subscription = watch(() => handleSubmit(onSubmit)());
    return () => subscription.unsubscribe();
  }, [handleSubmit, onSubmit, watch]);

  return (
    <div className={styles.relativeContainer}>
      <BaseErrors errors={baseErrors} />
      <Controller
        // @ts-ignore
        control={control}
        name="nextConsult"
        render={({ field: { onChange } }) => (
          <IonPopover
            className={styles.datePopover}
            event={popoverStateEvent}
            isOpen={dateOpener.isOpen}
            keepContentsMounted={true}
            onDidDismiss={dateOpener.close}
            showBackdrop={false}
            side="right"
          >
            <IonDatetime
              aria-describedby={formState.errors.nextConsult ? "nextConsult-or-error" : ""}
              aria-invalid={formState.errors.nextConsult?.message?.length ? "true" : "false"}
              aria-label={intl.formatMessage({ id: "forms.consultReport.nextConsultLabel" })}
              className={styles.dateInput}
              name="nextConsult"
              onIonChange={(event: CustomEvent<DatetimeChangeEventDetail>) => {
                let newValue = event.detail.value;
                if (newValue && typeof newValue === "string") {
                  newValue = newValue.split("T")[0];
                }
                if (newValue && newValue !== nextConsultValue) {
                  onChange(event.detail.value);
                }
              }}
              presentation="date"
              showDefaultButtons
              value={nextConsultValue}
            />
          </IonPopover>
        )}
      />
      <CircleButton
        ariaLabelKey="accessibility.ariaLabels.edit"
        fill="clear"
        icon="edit"
        onClick={e => {
          setPopoverStateEvent(e);
          dateOpener.open();
        }}
        size="medium"
      />
    </div>
  );
};

export default NextConsultForm;
